import {
    cleanRepeatable,
    prepareOverviewForApi,
    withSavedDates,
    SAVED_DATES_KEY,
    DEPARTURE_DATE_KEYS,
    ALLOCATION_DATE_KEYS,
} from './utils/formHelpers';
import { sortDeparturesByDate } from './utils/tripDates';

const toIntOrNull = (v) => (v === '' || v == null ? null : Number(v));

export const STEP_PAYLOAD = {
    overview: (fd) => prepareOverviewForApi(fd.overview || {}),

    packages: (fd) => {
        const { package_id, package_hero_image, status, image_gallery = [], ...rest } = fd.packages || {};
        return {
            ...rest,
            sequence: toIntOrNull(rest.sequence),
            duration: toIntOrNull(rest.duration),
            trip_ids: (rest.trip_ids || []).map(Number),
            image_gallery: image_gallery.map(({ pendingFile, ...item }) => item),
        };
    },

    pricing: (fd) => {
        const { base_price, currency, payment_templates } = fd.pricing || {};
        return { package_id: fd.packages?.package_id, base_price, currency, payment_templates };
    },

    services: (fd) => ({ services: fd.services?.services || [] }),
    departures: (fd) => ({
        departures: sortDeparturesByDate(
            (fd.departures?.departures || []).map(({ [SAVED_DATES_KEY]: _saved, ...d }) => ({
                ...d,
                hss_template: toIntOrNull(d.hss_template),
                hss_percentage: d.hss_percentage === '' ? null : d.hss_percentage,
                hss_flat_charge: d.hss_flat_charge === '' ? null : d.hss_flat_charge,
                departure_city: d.departure_city || null,
                book_by_date: d.book_by_date || null,
                return_date: d.return_date || null,
            }))
        ),
    }),
    addons: (fd) => ({ addons: fd.addons?.addons || [] }),
    faqs: (fd) => ({ faqs: fd.faqs?.faqs || [] }),

    allocations: (fd) => ({
        // Multi-select departures expand to one allocation row per departure_date_id.
        allocations: cleanRepeatable(fd.allocations?.allocations || []).flatMap((alloc) => {
            const isDated = (alloc.pricing_model || 'Dated') === 'Dated';
            const requested = alloc.min_number === '' || alloc.min_number == null ? null : Number(alloc.min_number);
            const depIds = Array.isArray(alloc.departure_date_ids) && alloc.departure_date_ids.length
                ? alloc.departure_date_ids
                : (alloc.departure_date_id != null && alloc.departure_date_id !== ''
                    ? [alloc.departure_date_id]
                    : []);
            const base = {
                ...alloc,
                merchant_id: toIntOrNull(alloc.merchant_id),
                package_component_id: toIntOrNull(alloc.package_component_id),
                trip_package_id: toIntOrNull(alloc.trip_package_id) ?? fd.packages?.package_id ?? null,
                rejected_on: alloc.rejected_on || null,
                rejection_reason: alloc.rejection_reason || null,
                season_from: isDated ? (alloc.season_from || null) : null,
                season_to: isDated ? (alloc.season_to || null) : null,
                cost: alloc.cost === '' || alloc.cost == null ? 0 : Number(alloc.cost),
                min_number: Number.isFinite(requested) ? requested : null,
                priority: alloc.priority ?? 0,
            };
            delete base.departure_date_ids;
            delete base[SAVED_DATES_KEY];
            delete base.season_bound_from;
            delete base.season_bound_to;
            if (!depIds.length) {
                return [{ ...base, departure_date_id: null }];
            }
            return depIds.map((depId, index) => {
                const row = {
                    ...base,
                    // Keep the existing row id on the first selected departure only;
                    // extra departures become new allocation rows.
                    id: index === 0 ? alloc.id : undefined,
                    departure_date_id: toIntOrNull(depId),
                };
                // Extra rows must not inherit Accepted/Rejected from the source —
                // they have never been sent to the merchant.
                if (index > 0) {
                    row.status = 'Pending';
                    row.rejection_reason = null;
                    row.rejected_on = null;
                }
                return row;
            });
        }),
    }),

    itineraries: (fd) => {
        const it = fd.itineraries || {};
        return {
            id: it.id,
            itinerary_title: it.itinerary_title,
            inclusion: it.inclusion,
            exclusion: it.exclusion,
            days: (it.days || []).map((day) => ({
                id: day.id || undefined,
                day_number: day.day_number,
                day_title: day.day_title,
                line_items: cleanRepeatable(day.line_items).map((li) => ({
                    ...li,
                    trip_component_map: toIntOrNull(li.trip_component_map),
                    start_date_time: li.start_date_time || null,
                    end_date_time: li.end_date_time || null,
                })),
            })),
        };
    },
};

const list = (d, k) => (Array.isArray(d) ? d : d?.[k] || []);
const setList = (step, key) => (fd, d) => ({ ...fd, [step]: { ...fd[step], [key]: list(d, key) } });

const normalizeAllocationForForm = (a) => {
    const depId = a?.departure_date_id;
    const hasDep = depId != null && depId !== '';
    return withSavedDates({
        ...a,
        departure_date_ids: Array.isArray(a?.departure_date_ids) && a.departure_date_ids.length
            ? a.departure_date_ids
            : (hasDep ? [depId] : []),
        // Bounds are derived from selected departures (incl. return date), not the
        // stored season window — seeding from season_from/to locked the picker.
        season_bound_from: a?.season_bound_from || '',
        season_bound_to: a?.season_bound_to || '',
    }, ALLOCATION_DATE_KEYS);
};

export const APPLY_RESPONSE = {
    overview: (fd, d) => fd,                                  // id handled in saveStep (d.id)
    packages: (fd, d) => ({ ...fd, packages: { ...fd.packages, package_id: d.package_id ?? fd.packages?.package_id } }),
    services: setList('services', 'services'),
    departures: (fd, d) => ({
        ...fd,
        departures: {
            ...fd.departures,
            departures: list(d, 'departures').map((dep) => withSavedDates(dep, DEPARTURE_DATE_KEYS)),
        },
    }),
    allocations: (fd, d) => ({
        ...fd,
        allocations: {
            ...fd.allocations,
            allocations: list(d, 'allocations').map(normalizeAllocationForForm),
        },
    }),
    addons: setList('addons', 'addons'),
    faqs: setList('faqs', 'faqs'),                            // FAQs return a plain list
    itineraries: (fd, d) => ({ ...fd, itineraries: { ...fd.itineraries, id: d.id, days: d.days ?? fd.itineraries?.days } }),
};